import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { CurrencyDisplay } from "./CurrencySettings";
import { 
  Building2, 
  Plus, 
  Edit2, 
  Trash2, 
  Calendar,
  Briefcase,
  Settings,
  Tag
} from "lucide-react";

const HeadOfficeExpenses = ({ user }) => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const [filterMonth, setFilterMonth] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  
  const [formData, setFormData] = useState({
    month: new Date().toISOString().slice(0, 7),
    category: "",
    description: "",
    amount: ""
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'superuser' || user?.role === 'manager';

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [filterMonth, filterCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get("/ho-expense-categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      let url = "/head-office-expenses?";
      if (filterMonth) url += `month=${filterMonth}&`;
      if (filterCategory !== "all") url += `category=${filterCategory}&`;
      const response = await axiosInstance.get(url);
      setExpenses(response.data);
    } catch (error) {
      toast.error("Failed to fetch expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.month || !formData.category || !formData.description || !formData.amount) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      if (editingExpense) {
        await axiosInstance.put(`/head-office-expenses/${editingExpense.id}`, {
          month: formData.month,
          category: formData.category,
          description: formData.description,
          amount: parseFloat(formData.amount)
        });
        toast.success("Expense updated successfully");
      } else {
        await axiosInstance.post("/head-office-expenses", {
          ...formData,
          amount: parseFloat(formData.amount)
        });
        toast.success("Expense added successfully");
      }
      fetchExpenses();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save expense");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/head-office-expenses/${id}`);
      toast.success("Expense deleted");
      setDeleteTarget(null);
      fetchExpenses();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      month: expense.month,
      category: expense.category,
      description: expense.description,
      amount: expense.amount.toString()
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingExpense(null);
    setFormData({
      month: new Date().toISOString().slice(0, 7),
      category: "",
      description: "",
      amount: ""
    });
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axiosInstance.post("/ho-expense-categories", { name: newCategoryName.trim() });
      toast.success("Category added");
      setNewCategoryName("");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add category");
    }
  };

  const handleUpdateCategory = async () => {
    if (!editCategoryName.trim() || !editingCategory) return;
    try {
      await axiosInstance.put(`/ho-expense-categories/${editingCategory.id}`, { name: editCategoryName.trim() });
      toast.success("Category updated");
      setEditingCategory(null);
      setEditCategoryName("");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update category");
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axiosInstance.delete(`/ho-expense-categories/${id}`);
      toast.success("Category deleted");
      setDeleteTarget(null);
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const categoryTotals = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});

  const categoryNames = categories.map(c => c.name);

  return (
    <div className="space-y-6" data-testid="head-office-expenses">
      {/* Header */}
      <Card className="shadow-lg border-0 bg-gradient-to-br from-indigo-50 to-purple-50">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Building2 className="w-6 h-6" />
                Head Office Expenses
              </CardTitle>
              <CardDescription className="text-indigo-100 mt-1">
                Common expenses shared across all restaurants
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {isAdmin && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCategoryDialogOpen(true)}
                  className="gap-2"
                  data-testid="manage-ho-categories-btn"
                >
                  <Settings className="w-4 h-4" />
                  Categories
                </Button>
              )}
              {isAdmin && (
                <Button 
                  onClick={() => setDialogOpen(true)}
                  className="gap-2 bg-white text-indigo-700 hover:bg-indigo-50"
                  data-testid="add-expense-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Expense
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Month</Label>
              <Input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="cursor-pointer"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categoryNames.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Expenses</p>
            <p className="text-2xl font-bold text-indigo-700">
              <CurrencyDisplay amount={totalExpenses} />
            </p>
            <p className="text-xs text-gray-500 mt-1">{expenses.length} entries</p>
          </CardContent>
        </Card>
        {Object.entries(categoryTotals).slice(0, 3).map(([category, total]) => (
          <Card key={category} className="bg-white">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 truncate">{category}</p>
              <p className="text-xl font-bold text-gray-900">
                <CurrencyDisplay amount={total} />
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Expenses Table */}
      <Card className="shadow-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading expenses...</div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Expenses Found</p>
              <p className="text-sm mt-1">Add head office expenses to track common costs</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Month</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {expense.month ? (() => { const [y, m] = expense.month.split('-'); const months = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${months[parseInt(m)]}-${y}`; })() : expense.month}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                          {expense.category}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[300px]">{expense.description}</TableCell>
                    <TableCell className="text-right font-semibold text-indigo-600">
                      <CurrencyDisplay amount={expense.amount} />
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(expense)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteTarget({ type: 'expense', id: expense.id })}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{editingExpense ? "Edit Expense" : "Add Head Office Expense"}</DialogTitle>
            <DialogDescription>Enter the monthly head office expense details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month *</Label>
                <Input type="month" value={formData.month} onChange={(e) => setFormData({ ...formData, month: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categoryNames.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea placeholder="Describe the expense" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button type="submit">{editingExpense ? "Update" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-md" data-testid="manage-categories-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Manage Expense Categories
            </DialogTitle>
            <DialogDescription>Add, edit, or remove head office expense categories</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add new */}
            <div className="flex gap-2">
              <Input
                placeholder="New category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                data-testid="new-category-input"
              />
              <Button onClick={handleAddCategory} disabled={!newCategoryName.trim()} data-testid="add-category-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Edit inline */}
            {editingCategory && (
              <div className="flex gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Input
                  value={editCategoryName}
                  onChange={(e) => setEditCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateCategory()}
                />
                <Button size="sm" onClick={handleUpdateCategory}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => { setEditingCategory(null); setEditCategoryName(""); }}>Cancel</Button>
              </div>
            )}

            {/* Category list */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {categories.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No categories added yet</p>
              ) : (
                categories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{cat.name}</span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => { setEditingCategory(cat); setEditCategoryName(cat.name); }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteTarget({ type: 'category', id: cat.id })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'category'
                ? "This will permanently delete this category. This action cannot be undone."
                : "This will permanently delete this expense entry. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget?.type === 'category'
                ? handleDeleteCategory(deleteTarget.id)
                : handleDelete(deleteTarget.id)
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HeadOfficeExpenses;
